import { useCallback, useEffect, useState } from "react"
import type { AppShell } from "@/App"
import {
  listComments,
  createComment,
  listAppUsers,
  getCurrentUser,
  authorLabel,
  type OrderComment,
  type CurrentUser,
} from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { MessageSquare, Send } from "lucide-react"

interface Props {
  shell: AppShell | null
  orderId: string
}

function fmt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })
}

/** Painel de comentários da encomenda — partilhado entre o detalhe da encomenda
 *  e o detalhe de logística (mesmo fio). */
export function OrderComments({ shell, orderId }: Props) {
  const [comments, setComments] = useState<OrderComment[]>([])
  const [users, setUsers] = useState<Map<string, string>>(new Map())
  const [me, setMe] = useState<CurrentUser | null>(null)
  const [draft, setDraft] = useState("")
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    if (!shell || !orderId) return
    try {
      const [rows, u, cur] = await Promise.all([
        listComments(shell, orderId),
        listAppUsers(shell),
        getCurrentUser(shell),
      ])
      setComments(rows)
      setUsers(u)
      setMe(cur)
    } catch {
      /* silencioso — o painel não deve rebentar o ecrã */
    }
  }, [shell, orderId])

  useEffect(() => {
    void load()
  }, [load])

  async function send() {
    if (!shell || !draft.trim()) return
    setSending(true)
    try {
      await createComment(shell, orderId, draft, me?.name ?? null)
      setDraft("")
      await load()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0 pb-2">
        <MessageSquare className="size-4 text-muted-foreground" />
        <CardTitle>Comentários ({comments.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {comments.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem comentários.</p>
        ) : (
          <ul className="space-y-2.5">
            {comments.map((c) => (
              <li key={c.id} className="rounded-md border bg-muted/30 px-3 py-2">
                <div className="mb-0.5 flex items-baseline gap-2 text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {authorLabel(c.created_by_name, c.created_by, users)}
                  </span>
                  <span>{fmt(c.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm">{c.body}</p>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter envia.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void send()
            }}
            rows={2}
            placeholder="Escrever um comentário…"
            className="flex-1 resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button
            size="sm"
            className="self-end"
            disabled={sending || !draft.trim()}
            onClick={() => void send()}
          >
            <Send className="mr-1.5 size-3.5" />
            {sending ? "A enviar…" : "Enviar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
